import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, User } from "lucide-react";
import { toast } from "sonner";
import { fmtFechaHora } from "@/lib/fechas";
import { LEN } from "@/lib/limites";

export default function DecaNotes({ decaId }) {
  const [notas, setNotas] = useState([]);
  const [texto, setTexto] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/api/v1/decas/${decaId}/notas`);
      setNotas(data.items || []);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => { load(); const iv = setInterval(load, 20_000); return () => clearInterval(iv); }, [decaId]); // eslint-disable-line
  useEffect(() => { listRef.current && (listRef.current.scrollTop = listRef.current.scrollHeight); }, [notas.length]);

  const send = async () => {
    const t = texto.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.post(`/api/v1/decas/${decaId}/notas`, { texto: t });
      setTexto("");
      load();
    } catch (e) {
      toast.error("No se pudo enviar la nota");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="deca-notes" className="min-w-0 w-full">
      <div className="label-tag mb-2 flex items-center gap-1"><MessageSquare className="h-3 w-3"/> Notas compartidas</div>
      <div ref={listRef} className="max-h-64 overflow-y-auto overflow-x-hidden border border-slate-200 rounded-md bg-slate-50 p-2 mb-2 space-y-2 min-w-0">
        {notas.length === 0 ? (
          <div className="text-xs text-slate-500 text-center py-4">Aún no hay notas. Escribe la primera.</div>
        ) : notas.map((n) => (
          <div key={n.id} className={`p-2 rounded-md min-w-0 max-w-full ${n.autor_rol === "conductor" ? "bg-blue-50 border border-blue-100" : "bg-white border border-slate-200"}`} data-testid={`nota-${n.id}`}>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
              <User className="h-3 w-3 shrink-0"/>
              <span className="font-semibold text-slate-700 truncate">{n.autor_nombre}</span>
              <span className="uppercase tracking-wider text-[9px] bg-slate-100 border border-slate-200 px-1 rounded shrink-0">{n.autor_rol}</span>
              <span className="ml-auto font-mono shrink-0">{fmtFechaHora(n.created_at)}</span>
            </div>
            <div className="text-sm mt-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-slate-900">{n.texto}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 min-w-0">
        <Textarea className="min-w-0" value={texto} maxLength={LEN.nota} onChange={(e)=>setTexto(e.target.value)} placeholder="Escribe una nota…" rows={2} data-testid="nota-input"/>
        <Button onClick={send} disabled={busy || !texto.trim()} className="bg-blue-700 hover:bg-blue-800 self-end shrink-0" data-testid="btn-nota-send">
          <Send className="h-4 w-4"/>
        </Button>
      </div>
    </div>
  );
}
